import { useModals } from '@/composables/useModal/useModal'
import constants from '@/exports/constants'
import helpers from '@/exports/helper'
import httpClient from '@/httpClient'
import {
  CallingCallDirection,
  CallingCallType,
  CallingFilter,
  CreateRoomError
} from '@/models/enums'
import type {
  IBuyMinutesPostRequest,
  ICallingDownload,
  ICallingItem,
  ICallLog,
  ICallLogRecordingInfo,
  ICallStatusCallback,
  ICompleteCallingSetupRequest,
  ICompleteRoomRequest,
  IFirstLegPostRequest,
  IGatherRequest,
  IGetCallsRequest,
  IGetVoicemailsRequest,
  IJoinRoomRequest,
  ILogActionRequest,
  INewSessionParam,
  IPhoneCallDetails,
  ISegment,
  ISendCoparentReminderRequest,
  ISetAllowsVideoCallsRequest,
  ISingleCallReportParam,
  ITwilioProxyRecordingCallback,
  IUpdateCallRequest,
  IUpdateVideoCallStatusRequest,
  IUpdateVoicemailStatusRequest,
  IVideoCall,
  WorkerRequest
} from '@/models/interfaces'
import type { ICreateRoomRequest, IJoinRoomResult } from '@/models/modelsV3'
import type { ICallingState } from '@/models/stores/calling'
import WorkerService from '@/services/webWorkerService'
import { watchIgnorable } from '@vueuse/core'
import { DateTime } from 'luxon'
import { defineStore } from 'pinia'
import { isSupported } from 'twilio-video'
import { computed, reactive, toRefs, watch } from 'vue'
import ErrorHelper from '../exports/error'
import fileDownloaderWorker from '../workers/fileDownloader?worker&url'
import { useAccountSettingsStore } from './AccountSettingsStore'
import { useCommonStore } from './CommonStore'
import { CreateSessionError } from '@/pages/calling/config/calling.enums'

const baseUrl = '/mobile/api'

const callingControllerBaseUrl = `${baseUrl}/calling`
const buyMinutesControllerBaseUrl = `${baseUrl}/buyminutes`
const callLogControllerBaseUrl = `${baseUrl}/calllog`
const firstLegControllerBaseUrl = `${baseUrl}/firstleg`
const outOfSessionControllerBaseUrl = `${baseUrl}/outofsession`
const recordingControllerBaseUrl = `${baseUrl}/recording`
const secondLegControllerBaseUrl = `${baseUrl}/secondleg`
const sessionControllerBaseUrl = `${baseUrl}/session`

const videoCallingControllerBaseUrl = `${baseUrl}/videocalling`

const singleCallReportControllerBaseUrl = `${baseUrl}/singlecallreport`
const singleVideoCallReportControllerBaseUrl = `${baseUrl}/singlevideocallreport`
const singleVoicemailReportControllerBaseUrl = `${baseUrl}/singlevoicemailreport`

const paths: string[] = ['videoInputId', 'audioInputId', 'audioOutputId']
const initialState = (): ICallingState => ({
  showVideoCall: false,
  videoCallCheckId: null,
  videoCallThreadId: null,
  callThreadId: null,
  startVideoCall: false,
  roomResult: null,
  activeCall: null,
  canCallStatus: new Map([
    [constants.CAN_CALL_STATUS.videoAllowed, false],
    [constants.CAN_CALL_STATUS.phoneAllowed, false],
    [constants.CAN_CALL_STATUS.coParentPhoneDisabled, false],
    [constants.CAN_CALL_STATUS.coParentVideoDisabled, false],
    [constants.CAN_CALL_STATUS.notPremium, false],
    [constants.CAN_CALL_STATUS.phoneCallingDisabled, false],
    [constants.CAN_CALL_STATUS.videoCallingDisabled, false],
    [constants.CAN_CALL_STATUS.coParentTierFree, false],
    [constants.CAN_CALL_STATUS.noMinutes, false]
  ]),
  callingItems: [
    {
      callType: CallingCallType.Phone,
      direction: CallingCallDirection.Outgoing,
      itemID: 0,
      caseID: 0,
      userID: 0,
      toUserID: 0,
      createdWhen: DateTime.now(),
      startTime: DateTime.now(),
      endTime: DateTime.now(),
      viewedWhen: DateTime.now(),
      status: '',
      isMissedCall: false,
      isUnseen: false,
      skeletonLoading: true
    },
    {
      callType: CallingCallType.Phone,
      direction: CallingCallDirection.Outgoing,
      itemID: 0,
      caseID: 0,
      userID: 0,
      toUserID: 0,
      createdWhen: DateTime.now(),
      startTime: DateTime.now(),
      endTime: DateTime.now(),
      viewedWhen: DateTime.now(),
      status: '',
      isMissedCall: false,
      isUnseen: false,
      skeletonLoading: true
    },
    {
      callType: CallingCallType.Phone,
      direction: CallingCallDirection.Outgoing,
      itemID: 0,
      caseID: 0,
      userID: 0,
      toUserID: 0,
      createdWhen: DateTime.now(),
      startTime: DateTime.now(),
      endTime: DateTime.now(),
      viewedWhen: DateTime.now(),
      status: '',
      isMissedCall: false,
      isUnseen: false,
      skeletonLoading: true
    }
  ],
  callingPageSize: 25,
  callingPageNumber: 1,
  loadMoreCallingItems: false,
  selectedCallingItem: null,
  selectedCallingFilter: CallingFilter.All,
  callingSearchTerm: '',
  coparentCallingSettings: null,
  showDetailsMenu: false,
  hasCompletedCallingSetup: true,
  showCallingSetup: false,
  introVideoDiagnostics: false,
  sentReminderWhen: null,
  showAudioCall: false,
  proxyPhoneNumber: 0,
  downloads: null,
  videoInputId: '',
  audioInputId: '',
  audioOutputId: '',
  unseenVoicemailCount: 0,
  videoCallDeclined: false,
  showVideoCallConfirmation: false,
  showVideoCallError: false,
  phoneSessionError: null
})

export const useCallingStore = defineStore(
  'calling',
  () => {
    //Options syntax, Composition syntax
    //state, ref
    const state = reactive<ICallingState>(initialState())

    //getters, computed

    // resets whatever needs to be refetched when entering calling
    // not resetting anything that will be used during a video call
    function resetMainCallingFeature() {
      state.callingItems = [
        {
          callType: CallingCallType.Phone,
          direction: CallingCallDirection.Outgoing,
          itemID: 0,
          caseID: 0,
          userID: 0,
          toUserID: 0,
          createdWhen: DateTime.now(),
          startTime: DateTime.now(),
          endTime: DateTime.now(),
          viewedWhen: DateTime.now(),
          status: '',
          isMissedCall: false,
          isUnseen: false,
          skeletonLoading: true
        },
        {
          callType: CallingCallType.Phone,
          direction: CallingCallDirection.Outgoing,
          itemID: 0,
          caseID: 0,
          userID: 0,
          toUserID: 0,
          createdWhen: DateTime.now(),
          startTime: DateTime.now(),
          endTime: DateTime.now(),
          viewedWhen: DateTime.now(),
          status: '',
          isMissedCall: false,
          isUnseen: false,
          skeletonLoading: true
        },
        {
          callType: CallingCallType.Phone,
          direction: CallingCallDirection.Outgoing,
          itemID: 0,
          caseID: 0,
          userID: 0,
          toUserID: 0,
          createdWhen: DateTime.now(),
          startTime: DateTime.now(),
          endTime: DateTime.now(),
          viewedWhen: DateTime.now(),
          status: '',
          isMissedCall: false,
          isUnseen: false,
          skeletonLoading: true
        }
      ]

      state.callingPageSize = 25
      state.callingPageNumber = 1
      state.loadMoreCallingItems = false
      state.selectedCallingItem = null
      state.selectedCallingFilter = CallingFilter.All
      state.callingSearchTerm = ''
      state.showDetailsMenu = false
      state.hasCompletedCallingSetup = true
      state.showCallingSetup = false
    }

    const newCallIsAvailable = computed(() => {
      return state.activeCall
    })

    const isBrowserSupportedForTwilio = computed(() => {
      return isSupported
    })

    const groupedCallingItems = computed(() => {
      const groupedMap = new Map()
      const commonStore = useCommonStore()
      const tz = commonStore.fullUserInfo.timeZoneNameForMomentJs
      for (const item of state.callingItems) {
        const startOfDay = DateTime.fromISO(
          item.createdWhen as unknown as string,
          { zone: tz }
        )
          .startOf('day')
          .toISO()

        let thisList = groupedMap.get(startOfDay)
        if (thisList === undefined) {
          thisList = []
          groupedMap.set(startOfDay, thisList)
        }
        thisList.push(item)
      }

      return Array.from(groupedMap.values()) as ICallingItem[][]
    })

    const isActiveCallIncoming = computed(() => {
      const commonStore = useCommonStore()
      return state.activeCall?.toUserID == commonStore.fullUserInfo.userId
    })

    const initialLoad = computed(
      () => !!state.callingItems.find((ci) => ci.skeletonLoading)
    )

    const showMobileHeaderCog = computed(
      () =>
        !state.showAudioCall &&
        !state.selectedCallingItem &&
        !state.showVideoCallError &&
        !state.showCallingSetup
    )

    //actions, functions

    function reset() {
      Object.assign(
        state,
        helpers.omit(initialState(), paths as (keyof ICallingState)[])
      )
    }

    function isFirstInGroup(itemID: number) {
      const foo = groupedCallingItems.value.reduce((a, val) => {
        a.push(val[0])
        return a
      }, [])
      return foo.find((i) => i.itemID == itemID) != undefined
    }

    function startCheckingForNewCall() {
      state.videoCallCheckId = setInterval(() => {
        getActiveVideoCall()
      }, 1000 * 60) as unknown as number
    }

    function setShowVideoCall(val: boolean) {
      state.showVideoCall = val
    }

    function setShowVideoCallConfirmation(val: boolean) {
      state.showVideoCallConfirmation = val
    }

    function setShowVideoCallError(val: boolean) {
      state.showVideoCallError = val
    }

    function setStartVideoCall(val: boolean) {
      state.startVideoCall = val
    }

    function setCallingItems(items: ICallingItem[]) {
      state.callingItems = items
    }

    function resetCallingItems() {
      setCallingItems([
        {
          callType: CallingCallType.Phone,
          direction: CallingCallDirection.Outgoing,
          itemID: 0,
          caseID: 0,
          userID: 0,
          toUserID: 0,
          createdWhen: DateTime.now(),
          startTime: DateTime.now(),
          endTime: DateTime.now(),
          viewedWhen: DateTime.now(),
          status: '',
          isMissedCall: false,
          isUnseen: false,
          skeletonLoading: true
        },
        {
          callType: CallingCallType.Phone,
          direction: CallingCallDirection.Outgoing,
          itemID: 0,
          caseID: 0,
          userID: 0,
          toUserID: 0,
          createdWhen: DateTime.now(),
          startTime: DateTime.now(),
          endTime: DateTime.now(),
          viewedWhen: DateTime.now(),
          status: '',
          isMissedCall: false,
          isUnseen: false,
          skeletonLoading: true
        },
        {
          callType: CallingCallType.Phone,
          direction: CallingCallDirection.Outgoing,
          itemID: 0,
          caseID: 0,
          userID: 0,
          toUserID: 0,
          createdWhen: DateTime.now(),
          startTime: DateTime.now(),
          endTime: DateTime.now(),
          viewedWhen: DateTime.now(),
          status: '',
          isMissedCall: false,
          isUnseen: false,
          skeletonLoading: true
        }
      ])
    }

    function setSelectedCallingItem(item: ICallingItem | null) {
      state.selectedCallingItem = item
    }

    function setLoadMoreCallingItems(loadMore: boolean) {
      state.loadMoreCallingItems = loadMore
    }

    function setSelectedCallingFilter(filter: CallingFilter) {
      state.selectedCallingFilter = filter
    }

    function setPageNumber(page: number) {
      state.callingPageNumber = page
    }

    function setActiveCall(payload: IVideoCall | null) {
      state.activeCall = payload
    }

    function setHasCompletedCallingSetup(hasCompleted: boolean) {
      state.hasCompletedCallingSetup = hasCompleted
    }

    function setIntroVideoDiagnostics(show: boolean) {
      state.introVideoDiagnostics = show
    }

    function setShowCallingSetup(show: boolean) {
      state.showCallingSetup = show
    }

    function setSentReminderWhen(date: Date | null) {
      state.sentReminderWhen = date
    }

    function setShowAudioCall(show: boolean) {
      state.showAudioCall = show
    }

    async function fetchCallingItems() {
      //page number is zero-based
      const items = (
        await fetchCalls({
          filter: state.selectedCallingFilter,
          pageNumber: state.callingPageNumber - 1,
          searchTerm: state.callingSearchTerm,
          pageSize: state.callingPageSize
        })
      )?.data.calls as ICallingItem[]

      //first page? overwrite, otherwise, append
      setCallingItems(
        state.callingPageNumber == 1 ? items : [...state.callingItems, ...items]
      )
      setLoadMoreCallingItems(items.length == state.callingPageSize)
    }

    async function fetchMoreCallingItems() {
      await fetchCallingItemsByPage(++state.callingPageNumber)
    }

    async function refreshNewCallingItems() {
      const items =
        ((
          await fetchCalls({
            filter: state.selectedCallingFilter,
            pageNumber: 0,
            searchTerm: state.callingSearchTerm,
            pageSize: state.callingPageSize
          })
        )?.data.calls as ICallingItem[]) ?? []

      const existingIds = new Set(state.callingItems.map((ci) => ci.itemID))
      const newItems = items.filter((item) => !existingIds.has(item.itemID))
      if (newItems.length > 0) {
        setCallingItems([...newItems, ...state.callingItems])
      }
    }

    async function fetchCallingItemsByPage(page: number) {
      setPageNumber(page)

      await fetchCallingItems()
    }

    const { ignoreUpdates: ignoreFilterAndSearchUpdates } = watchIgnorable(
      [() => state.callingSearchTerm, () => state.selectedCallingFilter],
      async () => {
        setSelectedCallingItem(null)
        resetCallingItems()
        await fetchCallingItemsByPage(1)
      }
    )

    watch(
      () => state.callingItems,
      async (val) => {
        if (Array.isArray(val) && val.length > 0) {
          const commonStore = useCommonStore()
          commonStore.setShowingNoCount(false)
        } else if (
          state.selectedCallingFilter == CallingFilter.All &&
          !state.callingSearchTerm.length
        ) {
          const commonStore = useCommonStore()
          commonStore.setShowingNoCount(true)
        }
      }
    )

    async function fetchPhoneCallDetails(item: ICallingItem) {
      const callLog = (await fetchCallLog(item.itemID))?.data as ICallLog

      const recordingInfo = (await fetchCallLogRecordingInfo(item.itemID))?.data
        .value as ICallLogRecordingInfo

      callLog.recordingSid = recordingInfo.recordingSid
      callLog.recordingDuration = recordingInfo.recordingDuration

      let segments = null

      //recording sid, duration, and transcript job status should be fine to fetch on standard, this obviously shouldn't
      segments = (await fetchTranscriptSegments(item.itemID))?.data
        .value as ISegment[]

      // this only works for older calling items i think
      const jobStatus = (await fetchTranscriptJobStatus(callLog.recordingSid))
        ?.data as string

      return {
        callLog: callLog,
        segments: segments,
        transcriptJobStatus: jobStatus
      } as IPhoneCallDetails
    }

    async function fetchVideoCallDetails(item: ICallingItem) {
      const call =
        item.callType == CallingCallType.Video
          ? ((await getVideoCall(item.itemID)) as IVideoCall)
          : ((await getVideoVoicemail(item.itemID)) as IVideoCall)

      const commonStore = useCommonStore()

      let segments = null
      let recording = null

      switch (item.callType) {
        case CallingCallType.Video: {
          segments = await getVideoCallTranscript(item.itemID)

          //fine to fetch this recording object on standard - only contains information about the recording, not the recording itself (audio/video)
          recording = await getVideoCallRecording(item.itemID)
          break
        }
        case CallingCallType.Voicemail: {
          segments = await getVideoVoicemailTranscript(item.itemID)

          recording = await getVideoVoicemailRecording(item.itemID)

          updateVoicemailIsUnseenListItem(item.itemID, false)
          if (state.unseenVoicemailCount > 0 && !item.viewedWhen) {
            state.unseenVoicemailCount -= 1
            commonStore.setUnseenVoicemailBadgecount(state.unseenVoicemailCount)
          }

          call.missedVoicemailRecording = isMissedVoicemailRecording(
            call.itemID
          )

          break
        }
      }

      return { videoCall: call, segments: segments, recording: recording }
    }

    function isMissedVoicemailRecording(itemID: number): boolean {
      return state.callingItems.find(
        (callItem) =>
          callItem.callType == CallingCallType.Voicemail &&
          callItem.itemID == itemID
      )?.missedVoiceMailRecording
        ? true
        : false
    }

    function setShowDetailsMenu(show: boolean) {
      state.showDetailsMenu = show
    }

    function updateVoicemailIsUnseenListItem(itemID: number, unseen: boolean) {
      if (Array.isArray(state.callingItems)) {
        // index and length will be offset if the user has cancelled an upload in the queue
        const _index = state.callingItems.findIndex((c) => c.itemID == itemID)
        if (_index !== -1) {
          state.callingItems[_index].isUnseen = unseen
        }
      }
    }

    function setCallingSearchTerm(searchTerm: string) {
      state.callingSearchTerm = searchTerm
    }

    // API Calls
    //#region CallingController
    async function fetchCalls(payload: IGetCallsRequest) {
      const endpoint = `${callingControllerBaseUrl}/getcalls?Filter=${payload.filter}&PageNumber=${payload.pageNumber}&SearchTerm=${encodeURIComponent(payload.searchTerm)}&PageSize=${payload.pageSize}`

      try {
        return await httpClient.get(endpoint, { headers: { 'api-version': 3 } })
      } catch (e) {
        ErrorHelper.handleError(e, 'fetchCalls')
      }
    }

    async function fetchVoicemails(payload: IGetVoicemailsRequest) {
      const endpoint = `${callingControllerBaseUrl}/getvoicemails?PageNumber=${payload.pageNumber}&SearchTerm=${encodeURIComponent(payload.searchTerm)}`

      try {
        return await httpClient.get(endpoint, { headers: { 'api-version': 2 } })
      } catch (e) {
        ErrorHelper.handleError(e, 'fetchVoicemails')
      }
    }

    async function fetchUnseenVoicemails() {
      const endpoint = `${callingControllerBaseUrl}/getunseenvoicemails`

      try {
        return await httpClient.get(endpoint, { headers: { 'api-version': 2 } })
      } catch (e) {
        ErrorHelper.handleError(e, 'fetchUnseenVoicemails')
      }
    }

    async function fetchCoparentCallingSettings() {
      const endpoint = `${callingControllerBaseUrl}/getcoparentcallingsettings`

      try {
        const results = await httpClient.get(endpoint, {
          headers: { 'api-version': 3 }
        })
        state.coparentCallingSettings = results.data
      } catch (e) {
        ErrorHelper.handleError(e, 'fetchCoparentCallingSettings')
      }
    }

    //#region calling setup
    async function fetchHasCompleteCallingSetup() {
      const endpoint = `${callingControllerBaseUrl}/gethascompletecallingsetup`

      try {
        const result = await httpClient.get(endpoint, {
          headers: { 'api-version': 2 }
        })
        return result.data.hasCompleteCallingSetup
      } catch (e) {
        ErrorHelper.handleError(e, 'fetchHasCompleteCallingSetup')
      }
    }

    async function completeCallingSetup(payload: ICompleteCallingSetupRequest) {
      const endpoint = `${callingControllerBaseUrl}/completecallingsetup`

      try {
        await httpClient.post(endpoint, payload, {
          headers: { 'api-version': 2 }
        })
      } catch (e) {
        ErrorHelper.handleError(e, 'completeCallingSetup')
      }
    }

    async function skipCallingSetup() {
      const endpoint = `${callingControllerBaseUrl}/skipcallingsetup`

      try {
        return await httpClient.post(endpoint, {
          headers: { 'api-version': 2 }
        })
      } catch (e) {
        ErrorHelper.handleError(e, 'skipCallingSetup')
      }
    }

    async function sendCoparentReminder(payload: ISendCoparentReminderRequest) {
      const endpoint = `${callingControllerBaseUrl}/sendcoparentreminder`

      try {
        return await httpClient.post(endpoint, payload, {
          headers: { 'api-version': 2 }
        })
      } catch (e) {
        ErrorHelper.handleError(e, 'sendCoparentReminder')
      }
    }
    //#endregion calling setup

    //#region enable/get allows video calls
    async function fetchAllowsVideoCalls() {
      const endpoint = `${callingControllerBaseUrl}/fetchallowsvideocalls`

      try {
        return await httpClient.get(endpoint, { headers: { 'api-version': 2 } })
      } catch (e) {
        ErrorHelper.handleError(e, 'fetchAllowsVideoCalls')
      }
    }

    async function setAllowsVideoCalls(payload: ISetAllowsVideoCallsRequest) {
      const endpoint = `${callingControllerBaseUrl}/setallowsvideocalls`

      try {
        return await httpClient.post(endpoint, payload, {
          headers: { 'api-version': 2 }
        })
      } catch (e) {
        ErrorHelper.handleError(e, 'setAllowsVideoCalls')
      }
    }
    //#endregion enable/get allows video calls
    //#endregion CallingController

    //#region PhoneCalling/BuyMinutesController
    async function buyMinutesPost(payload: IBuyMinutesPostRequest) {
      const endpoint = buyMinutesControllerBaseUrl

      try {
        return await httpClient.post(endpoint, payload)
      } catch (e) {
        ErrorHelper.handleError(e, 'buyMinutes')
      }
    }

    async function fetchCallAddonMinutesPlans() {
      const endpoint = `${buyMinutesControllerBaseUrl}/getcalladdonminutesplans`

      try {
        return await httpClient.get(endpoint)
      } catch (e) {
        ErrorHelper.handleError(e, 'fetchCallAddonMinutesPlans')
      }
    }
    //#endregion

    //#region PhoneCalling/CallLogController
    async function fetchCallLogs() {
      const endpoint = `${callLogControllerBaseUrl}/getcalllogs`

      try {
        return await httpClient.get(endpoint)
      } catch (e) {
        ErrorHelper.handleError(e, 'fetchCallLogs')
      }
    }

    async function fetchCallLog(callLogItemId: number) {
      const endpoint = `${callLogControllerBaseUrl}/getcalllog?callLogItemID=${callLogItemId}`

      try {
        return await httpClient.get(endpoint)
      } catch (e) {
        ErrorHelper.handleError(e, 'fetchCallLog')
      }
    }

    async function fetchCallLogRecordingInfo(callLogItemId: number) {
      const endpoint = `${callLogControllerBaseUrl}/getcalllogrecordinginfo?callLogItemID=${callLogItemId}`

      try {
        return await httpClient.get(endpoint, { headers: { 'api-version': 2 } })
      } catch (e) {
        ErrorHelper.handleError(e, 'fetchCallLogRecordingInfo')
      }
    }

    async function fetchRecordingSasUrl(callLogItemId: number) {
      const endpoint = `${callLogControllerBaseUrl}/getrecordingsasurl?callLogItemID=${callLogItemId}`

      try {
        return await httpClient.get(endpoint, { headers: { 'api-version': 2 } })
      } catch (e) {
        ErrorHelper.handleError(e, 'fetchRecordingSasUrl')
      }
    }

    async function fetchTranscriptSegments(callLogItemId: number) {
      const endpoint = `${callLogControllerBaseUrl}/gettranscriptsegments?callLogItemID=${callLogItemId}`

      try {
        return await httpClient.get(endpoint, { headers: { 'api-version': 2 } })
      } catch (e) {
        ErrorHelper.handleError(e, 'fetchTranscriptSegments')
      }
    }

    async function fetchTranscriptJobStatus(recordingSid: string) {
      const endpoint = `${callLogControllerBaseUrl}/gettranscriptjobstatus?recordingSid=${recordingSid}`

      try {
        return await httpClient.get(endpoint)
      } catch (e) {
        ErrorHelper.handleError(e, 'fetchTranscriptJobStatus')
      }
    }
    //#endregion

    //#region PhoneCalling/FirstLegController
    async function firstLegPost(payload: IFirstLegPostRequest) {
      const endpoint = `${firstLegControllerBaseUrl}?session=${payload.session}&userToNumber=${payload.userToNumber}`

      try {
        return await httpClient.post(endpoint)
      } catch (e) {
        ErrorHelper.handleError(e, 'firstLegPost')
      }
    }

    async function firstLegGather(
      gatherRequest: IGatherRequest,
      session: string,
      attempt: number,
      userToNumber: string,
      proxyNumber: string
    ) {
      const endpoint = `${firstLegControllerBaseUrl}/firstleggather?session=${session}&attempt=${attempt}&userToNumber=${userToNumber}&proxyNumber=${proxyNumber}`

      try {
        return await httpClient.post(endpoint, gatherRequest)
      } catch (e) {
        ErrorHelper.handleError(e, 'firstLegGather')
      }
    }

    async function firstLegHold(
      session: string,
      userToNumber: string,
      proxyNumber: string
    ) {
      const endpoint = `${firstLegControllerBaseUrl}/firstleghold?session=${session}&userToNumber=${userToNumber}&proxyNumber=${proxyNumber}`

      try {
        return await httpClient.post(endpoint)
      } catch (e) {
        ErrorHelper.handleError(e, 'firstLegHold')
      }
    }

    async function firstLegConnect() {
      const endpoint = `${firstLegControllerBaseUrl}/firstlegconnect`

      try {
        return await httpClient.post(endpoint)
      } catch (e) {
        ErrorHelper.handleError(e, 'firstLegConnect')
      }
    }

    async function firstLegCallback(
      v: ICallStatusCallback,
      sessionSid: string
    ) {
      const endpoint = `${firstLegControllerBaseUrl}/firstlegcallback?sessionSid=${sessionSid}`

      try {
        return await httpClient.post(endpoint, v)
      } catch (e) {
        ErrorHelper.handleError(e, 'firstLegCallback')
      }
    }
    //#endregion FirstLegController

    //#region PhoneCalling/OutOfSessionController
    async function outOfSessionPost() {
      const endpoint = outOfSessionControllerBaseUrl

      try {
        return await httpClient.post(endpoint)
      } catch (e) {
        ErrorHelper.handleError(e, 'outOfSessionPost')
      }
    }
    //#endregion PhoneCalling/OutOfSessionController

    //#region PhoneCalling/RecordingController
    async function recordingCallback(
      v: ITwilioProxyRecordingCallback,
      sessionSid: string
    ) {
      const endpoint = `${recordingControllerBaseUrl}/recordingCallback?sessionSid=${sessionSid}`

      try {
        return await httpClient.post(endpoint, v)
      } catch (e) {
        ErrorHelper.handleError(e, 'recordingCallback')
      }
    }

    async function fetchDownloadUrl(nItemID: number) {
      const endpoint = `${recordingControllerBaseUrl}/getDownloadUrl?nItemID=${nItemID}`

      try {
        return await httpClient.get(endpoint)
      } catch (e) {
        ErrorHelper.handleError(e, 'fetchDownloadUrl')
      }
    }

    async function fetchPreviewdUrl(nItemID: number) {
      const endpoint = `${recordingControllerBaseUrl}/getPreviewdUrl?nItemID=${nItemID}`

      try {
        return await httpClient.get(endpoint)
      } catch (e) {
        ErrorHelper.handleError(e, 'fetchPreviewdUrl')
      }
    }
    //#endregion PhoneCalling/RecordingController

    //#region PhoneCalling/SecondLegController
    async function secondLegPost(session: string, attempt: number) {
      const endpoint = `${secondLegControllerBaseUrl}?session=${session}&attempt=${attempt}}`

      try {
        return await httpClient.post(endpoint)
      } catch (e) {
        ErrorHelper.handleError(e, 'secondLegPost')
      }
    }

    async function secondLegGather(
      gatherRequest: IGatherRequest,
      session: string,
      attempt: number,
      userToNumber: string,
      proxyNumber: string
    ) {
      const endpoint = `${secondLegControllerBaseUrl}/secondleggather?session=${session}&attempt=${attempt}&userToNumber=${userToNumber}&proxyNumber=${proxyNumber}`

      try {
        return await httpClient.post(endpoint, gatherRequest)
      } catch (e) {
        ErrorHelper.handleError(e, 'secondLegGather')
      }
    }

    async function secondLegCallback(
      v: ICallStatusCallback,
      sessionSid: string
    ) {
      const endpoint = `${secondLegControllerBaseUrl}/secondlegcallback?sessionSid=${sessionSid}`

      try {
        return await httpClient.post(endpoint, v)
      } catch (e) {
        ErrorHelper.handleError(e, 'secondLegCallback')
      }
    }
    //#endregion PhoneCalling/SecondLegController

    //#region PhoneCalling/SessionController
    async function sessionPost(nsp: INewSessionParam) {
      const endpoint = sessionControllerBaseUrl
      state.phoneSessionError = null

      try {
        const result = (
          await httpClient.post(endpoint, nsp, {
            headers: { 'api-version': '3.0' }
          })
        ).data

        if (result) {
          if (result.success) {
            state.proxyPhoneNumber = result.value
            return state.proxyPhoneNumber
          } else if (
            result.errorCode === CreateSessionError.CountryCodeNotSupported
          ) {
            state.phoneSessionError = CreateSessionError.CountryCodeNotSupported
          } else if (result.errorCode === CreateSessionError.CallInProgress) {
            ErrorHelper.handleError(
              new Error(result.errorMessage),
              'sessionPost',
              true,
              result.errorMessage
            )
          } else {
            throw new Error(result.errorMessage)
          }
        }
      } catch (e) {
        ErrorHelper.handleError(e, 'sessionPost')
      }
    }
    //#endregion PhoneCalling/SessionController

    //#region VideoCalling/VideoCallingController
    async function getTestAccessToken() {
      const endpoint = `${videoCallingControllerBaseUrl}/GetTestAccessToken`

      try {
        const result = await httpClient.get(endpoint, {
          headers: { 'api-version': 3 }
        })
        if (result) {
          return result
        }
      } catch (e) {
        ErrorHelper.handleError(e, 'getActiveVideoCall')
      }
    }

    async function createRoom(request: ICreateRoomRequest) {
      const endpoint = `${videoCallingControllerBaseUrl}/createRoom`

      try {
        const result = await httpClient.post(endpoint, request, {
          headers: { 'api-version': 3 }
        })
        state.roomResult = result.data
        if (!state.roomResult?.success) {
          if (errorIsUserIssue(state.roomResult?.errorCode ?? 0)) {
            setShowVideoCallError(true)
          } else {
            throw Error(state.roomResult?.errorMessage)
          }
        } else {
          return true
        }
      } catch (e) {
        ErrorHelper.handleError(e, 'createRoom')
        return false
      }
    }

    function errorIsUserIssue(errorCode: number) {
      // Not all of these are actually sent by the api when called createRoom, but adding it in case the api is updated
      setShowVideoCall(false)
      switch (errorCode) {
        case CreateRoomError.CoparentDisabledVideoCalls:
          setCanCallStatus(
            constants.CAN_CALL_STATUS.coParentVideoDisabled,
            true
          )
          setCanCallStatus(constants.CAN_CALL_STATUS.videoAllowed, false)
          return true
        case CreateRoomError.FeatureDisabled:
          setCanCallStatus(constants.CAN_CALL_STATUS.videoCallingDisabled, true)
          setCanCallStatus(constants.CAN_CALL_STATUS.videoAllowed, false)
          return true
        case CreateRoomError.NoMinutes:
          setCanCallStatus(constants.CAN_CALL_STATUS.noMinutes, true)
          setCanCallStatus(constants.CAN_CALL_STATUS.videoAllowed, false)
          return true
        case CreateRoomError.RecipientHasInvalidTier:
          setCanCallStatus(constants.CAN_CALL_STATUS.coParentTierFree, true)
          setCanCallStatus(constants.CAN_CALL_STATUS.videoAllowed, false)
          return true
        case CreateRoomError.UserNotPremium:
          setCanCallStatus(constants.CAN_CALL_STATUS.notPremium, true)
          setCanCallStatus(constants.CAN_CALL_STATUS.videoAllowed, false)
          return true
        default:
          return false
      }
    }

    async function getActiveVideoCall() {
      const endpoint = `${videoCallingControllerBaseUrl}/GetActiveVideoCall`

      try {
        const result = await httpClient.get(endpoint, {
          headers: { 'api-version': 2 }
        })
        if (result) {
          setActiveCall(result.data.value)
        }
        return true
      } catch (e) {
        ErrorHelper.handleError(e, 'getActiveVideoCall')
        return false
      }
    }

    async function joinRoom(
      request: IJoinRoomRequest
    ): Promise<IJoinRoomResult | undefined> {
      const endpoint = `${videoCallingControllerBaseUrl}/joinRoom?joinVideoCallItemID=${request.joinVideoCallItemID}`

      try {
        const result = await httpClient.get(endpoint, {
          headers: { 'api-version': 3 }
        })
        if (result) {
          state.roomResult = result.data
        }
        return result.data as IJoinRoomResult
      } catch (e) {
        ErrorHelper.handleError(e, 'joinRoom')
      }
    }

    async function completeRoom(request: ICompleteRoomRequest) {
      const endpoint = `${videoCallingControllerBaseUrl}/completeRoom?type=${request.type}&itemID=${request.itemID}`

      try {
        return await httpClient.get(endpoint, { headers: { 'api-version': 3 } })
      } catch (e) {
        ErrorHelper.handleError(e, 'completeRoom')
      }
    }

    async function updateCall(request: IUpdateCallRequest) {
      const endpoint = `${videoCallingControllerBaseUrl}/updateCall?itemID=${request.itemID}&callType=${request.callType}&newStatus=${request.newStatus}&action=${request.action}&actionData=${request.actionData}`

      try {
        return await httpClient.get(endpoint, { headers: { 'api-version': 3 } })
      } catch (e) {
        ErrorHelper.handleError(e, 'updateCall')
      }
    }

    async function updateVideoCallStatus(
      request: IUpdateVideoCallStatusRequest
    ) {
      const endpoint = `${videoCallingControllerBaseUrl}/updateVideoCallStatus?videoCallId=${request.videoCallId}&newStatus=${request.newStatus}`

      try {
        return await httpClient.get(endpoint, { headers: { 'api-version': 2 } })
      } catch (e) {
        ErrorHelper.handleError(e, 'updateVideoCallStatus')
      }
    }

    async function updateVoicemailStatus(
      request: IUpdateVoicemailStatusRequest
    ) {
      const endpoint = `${videoCallingControllerBaseUrl}/updateVoicemailStatus?voicemailID=${request.voicemailId}&newStatus=${request.newStatus}`

      try {
        return await httpClient.get(endpoint, { headers: { 'api-version': 2 } })
      } catch (e) {
        ErrorHelper.handleError(e, 'updateVoicemailStatus')
      }
    }

    async function logAction(payload: ILogActionRequest) {
      const endpoint = `${videoCallingControllerBaseUrl}/LogAction`

      try {
        return await httpClient.post(endpoint, payload, {
          headers: { 'api-version': 2 }
        })
      } catch (e) {
        ErrorHelper.handleError(e, 'completeCallingSetup')
      }
    }

    async function getVideoCall(itemID: number) {
      const endpoint = `${videoCallingControllerBaseUrl}/GetVideoCall?ItemID=${itemID}`

      try {
        const result = await httpClient.get(endpoint, {
          headers: { 'api-version': 2 }
        })
        if (result) {
          return result.data.value
        }
      } catch (e) {
        ErrorHelper.handleError(e, 'GetVideoCallRecording')
        return false
      }
    }

    async function getVideoCallRecording(itemId: number) {
      const endpoint = `${videoCallingControllerBaseUrl}/GetVideoCallRecording?ItemID=${itemId}`

      try {
        const result = await httpClient.get(endpoint, {
          headers: { 'api-version': 2 }
        })

        if (result) {
          return result.data.value
        }
      } catch (e) {
        ErrorHelper.handleError(e, 'getVideoCallRecording')
      }
    }

    async function getVideoVoicemailRecording(itemId: number) {
      const endpoint = `${videoCallingControllerBaseUrl}/GetVideoVoicemailRecording?ItemID=${itemId}`

      try {
        const result = await httpClient.get(endpoint, {
          headers: { 'api-version': 2 }
        })

        if (result) {
          return result.data.value
        }
      } catch (e) {
        ErrorHelper.handleError(e, 'getVideoCallRecording')
      }
    }

    async function getActiveVideoCallByItemID(itemID: number | undefined) {
      if (itemID == undefined) return
      const endpoint = `${videoCallingControllerBaseUrl}/GetVideoCall?ItemID=${itemID}`

      try {
        const result = await httpClient.get(endpoint, {
          headers: { 'api-version': 2 }
        })
        if (result) {
          setActiveCall(result.data.value)
        }
      } catch (e) {
        ErrorHelper.handleError(e, 'GetVideoCallRecording')
        return false
      }
    }
    // works for voicemails
    async function getVideoCallRecordingURL(channelID: string) {
      const endpoint = `${videoCallingControllerBaseUrl}/GetRecordingUrl?Channel=${channelID}`

      try {
        const result = await httpClient.get(endpoint, {
          headers: { 'api-version': 2 }
        })
        if (result) {
          return result.data.value
        }
      } catch (e) {
        ErrorHelper.handleError(e, 'GetRecordingUrl')
        return false
      }
    }

    async function getVideoCallTranscript(itemID: number) {
      const endpoint = `${videoCallingControllerBaseUrl}/GetVideoCallTranscript?ItemID=${itemID}`

      try {
        const result = await httpClient.get(endpoint, {
          headers: { 'api-version': 2 }
        })
        if (result) {
          return result.data.value
        }
      } catch (e) {
        ErrorHelper.handleError(e, 'GetVideoCallTranscript')
        return false
      }
    }

    //#endregion VideoCalling/VideoCallingController
    //#region Video Voicemails
    async function getVideoVoicemail(itemID: number) {
      const endpoint = `${videoCallingControllerBaseUrl}/GetVideoVoicemail?ItemID=${itemID}`

      try {
        const result = await httpClient.get(endpoint, {
          headers: { 'api-version': 2 }
        })
        if (result) {
          return result.data.value
        }
      } catch (e) {
        ErrorHelper.handleError(e, 'GetVideoVoicemail')
        return false
      }
    }

    async function getVideoVoicemailTranscript(itemID: number) {
      const endpoint = `${videoCallingControllerBaseUrl}/GetVideoVoicemailTranscript?ItemID=${itemID}`

      try {
        const result = await httpClient.get(endpoint, {
          headers: { 'api-version': 2 }
        })
        if (result) {
          return result.data.value
        }
      } catch (e) {
        ErrorHelper.handleError(e, 'GetVideoVoicemailTranscript')
        return false
      }
    }

    async function fetchUnseenVoicemailCount() {
      const endpoint = `${videoCallingControllerBaseUrl}/GetUnseenVoicemailCount`

      try {
        const result = await httpClient.get(endpoint, {
          headers: { 'api-version': 2 }
        })
        if (result) {
          state.unseenVoicemailCount = result.data.unseenVoicemailCount
          const commonStore = useCommonStore()
          commonStore.setUnseenVoicemailBadgecount(state.unseenVoicemailCount)
        }
      } catch (e) {
        ErrorHelper.handleError(e, 'GetVideoVoicemailTranscript')
        return false
      }
    }
    //#endregion

    //#region Precheck
    async function bothParentsPassPrecheck() {
      const commonStore = useCommonStore()
      resetCanCallStatus()
      setCanCallStatus(constants.CAN_CALL_STATUS.phoneAllowed, true)
      setCanCallStatus(constants.CAN_CALL_STATUS.videoAllowed, true)

      const accountSettingsStore = useAccountSettingsStore()

      // fetch phone info
      await accountSettingsStore.fetchPhoneInfo()
      // fetch calling balance
      await accountSettingsStore.fetchCallingBalance()
      // fetch coparent settings
      await fetchCoparentCallingSettings()

      if (
        accountSettingsStore.subscriptionT0 &&
        !commonStore.featureFlags?.web_freeplan
      ) {
        setCanCallStatus(constants.CAN_CALL_STATUS.notPremium, true) // subscription tier
        setCanCallStatus(constants.CAN_CALL_STATUS.phoneAllowed, false)
        setCanCallStatus(constants.CAN_CALL_STATUS.videoAllowed, false)
      }

      if (
        accountSettingsStore.callingBalance != null &&
        accountSettingsStore.callingBalance <= 0 &&
        !accountSettingsStore.subscriptionT2
      ) {
        setCanCallStatus(constants.CAN_CALL_STATUS.noMinutes, true) // not enough minutes
        setCanCallStatus(constants.CAN_CALL_STATUS.phoneAllowed, false)
        setCanCallStatus(constants.CAN_CALL_STATUS.videoAllowed, false)
      }

      if (
        !accountSettingsStore.phoneInfo.allowCallsFromMatchedUser ||
        !accountSettingsStore.phoneInfo.isUserMobilePhoneVerified
      ) {
        setCanCallStatus(constants.CAN_CALL_STATUS.phoneCallingDisabled, true) // disabled phone calling
        setCanCallStatus(constants.CAN_CALL_STATUS.phoneAllowed, false) // if checking for video call, this status does not matter
      }

      if (!accountSettingsStore.phoneInfo.allowsVideoCalls) {
        setCanCallStatus(constants.CAN_CALL_STATUS.videoCallingDisabled, true) // disabled video calling
        setCanCallStatus(constants.CAN_CALL_STATUS.videoAllowed, false)
      }

      if (!state.coparentCallingSettings?.allowsPhoneCalls) {
        setCanCallStatus(constants.CAN_CALL_STATUS.coParentPhoneDisabled, true) // coparent issue
        setCanCallStatus(constants.CAN_CALL_STATUS.phoneAllowed, false)
      }

      if (!state.coparentCallingSettings?.allowsVideoCalls) {
        setCanCallStatus(constants.CAN_CALL_STATUS.coParentVideoDisabled, true) // coparent issue
        setCanCallStatus(constants.CAN_CALL_STATUS.videoAllowed, false)
      }

      if (
        !state.coparentCallingSettings
          ?.isSubscriptionCapableOfReceivingVideoCall &&
        !commonStore.featureFlags?.web_freeplan
      ) {
        setCanCallStatus(constants.CAN_CALL_STATUS.coParentTierFree, true) // coparent issue
        setCanCallStatus(constants.CAN_CALL_STATUS.videoAllowed, false)
      }
    }

    async function tryShowVideoCall() {
      if (state.showVideoCall || state.showVideoCallError) return
      await bothParentsPassPrecheck()
      const canMakeVideoCall =
        state.canCallStatus.get(constants.CAN_CALL_STATUS.videoAllowed) ?? false

      if (canMakeVideoCall) {
        setShowVideoCall(true)
      } else {
        setShowVideoCallError(true)
      }
    }
    //#endregion

    //#region SingleCallReportController
    async function singleCallReportPost(p: ISingleCallReportParam) {
      try {
        const result = (
          await httpClient.post(singleCallReportControllerBaseUrl, p, {
            headers: { 'api-version': 2 }
          })
        ).data

        return result.success
      } catch (e) {
        ErrorHelper.handleError(e, 'singleCallReportPost')
        return false
      }
    }
    //#endregion SingleCallReportController
    //#region SingleVideoCallReportController
    async function singleVideoCallReportPost(p: ISingleCallReportParam) {
      try {
        const result = (
          await httpClient.post(singleVideoCallReportControllerBaseUrl, p, {
            headers: { 'api-version': 2 }
          })
        ).data

        return result.success
      } catch (e) {
        ErrorHelper.handleError(e, 'singleVideoCallReportPost')
        return false
      }
    }
    //#endregion SingleVideoCallReportController
    //#region SingleVoicemailReportController
    async function singleVoicemailReportPost(p: ISingleCallReportParam) {
      try {
        const result = (
          await httpClient.post(singleVoicemailReportControllerBaseUrl, p, {
            headers: { 'api-version': 2 }
          })
        ).data

        return result.success
      } catch (e) {
        ErrorHelper.handleError(e, 'singleVoicemailReportPost')
        return false
      }
    }
    //#endregion SingleVoicemailReportController

    function startDownload(
      url: string,
      fileName: string,
      callingItem: ICallingItem
    ) {
      const id = Date.now().toString() // Generate a unique ID for the download

      WorkerService.createWorker(id, fileDownloaderWorker)
      const options: WorkerRequest['options'] = {}
      const request: WorkerRequest = { url, options, id }

      WorkerService.postRequest(id, request, (response) => {
        if (response.success) {
          if (response.data.status === 'progress') {
            if (response.data.id === id) {
              const download = getDownloadFromList(id)
              if (download) {
                download.progress = response.data.progress
              }
            }
          } else if (response.data.status === 'completed') {
            const download = getDownloadFromList(id)
            if (download) {
              handleDownloadedFile(response.data.data, download.fileName)
              download.status = constants.vaultDownloadStatus.completed
            }
            WorkerService.terminateWorker(id)
          } else if (response.data.status === 'error') {
            const download = getDownloadFromList(id)
            if (download) {
              download.status = constants.vaultDownloadStatus.failed
            }
            WorkerService.terminateWorker(id)
          }
        } else {
          const download = getDownloadFromList(id)
          if (download) {
            download.status = constants.vaultDownloadStatus.failed
          }
        }
      })

      state.downloads = [
        ...(state.downloads ?? []),
        ...[
          {
            id,
            url,
            progress: 0,
            fileName,
            status: constants.vaultDownloadStatus.downloading,
            callingItem: callingItem
          }
        ]
      ]
    }

    function reDownload(existingFile: ICallingDownload) {
      //const workerUrl = new URL('../workers/fileDownloader.ts', import.meta.url)
      WorkerService.createWorker(existingFile.id, fileDownloaderWorker)
      const options: WorkerRequest['options'] = {}
      const request: WorkerRequest = {
        url: existingFile.url,
        options,
        id: existingFile.id
      }

      WorkerService.postRequest(existingFile.id, request, (response) => {
        if (response.success) {
          if (response.data.status === 'progress') {
            if (response.data.id === existingFile.id) {
              const download = getDownloadFromList(existingFile.id)
              if (download) {
                download.progress = response.data.progress
              }
            }
          } else if (response.data.status === 'completed') {
            const download = getDownloadFromList(existingFile.id)
            if (download) {
              handleDownloadedFile(response.data.data, download.fileName)
              download.status = constants.vaultDownloadStatus.completed
            }
            WorkerService.terminateWorker(existingFile.id)
          } else if (response.data.status === 'error') {
            const download = getDownloadFromList(existingFile.id)
            if (download) {
              download.status = constants.vaultDownloadStatus.failed
            }
            WorkerService.terminateWorker(existingFile.id)
          }
        } else {
          const download = getDownloadFromList(existingFile.id)
          if (download) {
            download.status = constants.vaultDownloadStatus.failed
          }
        }
      })
    }

    function handleDownloadedFile(blob: Blob, fileName: string) {
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    }

    function stopDownload(id: string) {
      const download = getDownloadFromList(id)
      if (download) {
        download.status = constants.vaultDownloadStatus.stopped
        WorkerService.terminateWorker(id)
      }
    }

    function removeDownloadFromList(id: string) {
      if (!state.downloads) {
        return
      }

      const downloadIndex = state.downloads.findIndex((d) => d.id === id)
      if (downloadIndex >= 0) {
        WorkerService.terminateWorker(id)
        state.downloads.splice(downloadIndex, 1)
      }
    }

    function getDownloadFromList(id: string) {
      return state.downloads?.find((d) => d.id === id)
    }

    function removeAllDownloads() {
      state.downloads?.forEach((d) => {
        if (d.status == constants.vaultDownloadStatus.downloading) {
          WorkerService.terminateWorker(d.id)
        }
      })
      state.downloads = null
    }

    const getInProgressDownloads = computed(() => {
      return state.downloads?.filter(
        (d) => d.status == constants.vaultDownloadStatus.downloading
      )
    })

    // download modals
    function cancelAllDownloads() {
      const downloadString = `<span class="place-center">Some items are still downloading. Are you sure you want to stop all of them?</span>`

      if (getInProgressDownloads.value?.length) {
        const { createSlot, closeModal, generateModal, HTMLtoComponent } =
          useModals()
        const el = generateModal({
          default: {
            headerText: 'Cancel downloads',
            footerButtonLabel: 'Confirm'
          },
          slot: {
            content: createSlot('content', HTMLtoComponent(downloadString))
              .content
          },
          config: {
            showHeader: true,
            showBody: true,
            showFooter: true,
            addContentPadding: true,
            closeOnConfirm: true,
            showCloseButton: true,
            secondaryCTALabel: 'Cancel'
          },
          callback: {
            confirmFn: () => {
              removeAllDownloads()
            },
            secondaryFn: () => {
              closeModal(el)
            }
          }
        })
      } else {
        removeAllDownloads()
      }
    }

    function setMediatId(
      type: 'videoinput' | 'audioinput' | 'audiooutput',
      id: string
    ) {
      switch (type) {
        case 'videoinput':
          state.videoInputId = id
          break
        case 'audioinput':
          state.audioInputId = id
          break
        case 'audiooutput':
          state.audioOutputId = id
          break
      }
    }

    function setVideoCallThreadId(val: number | null) {
      state.videoCallThreadId = val
    }

    function setCanCallStatus(statusKey: string, statusValue: boolean) {
      state.canCallStatus.set(statusKey, statusValue)
    }

    function resetCanCallStatus() {
      for (const key of state.canCallStatus.keys()) {
        state.canCallStatus.set(key, false)
      }
    }
    function setCallThreadId(val: number | null) {
      state.callThreadId = val
    }

    function setProxyPhoneNumber(val: number) {
      state.proxyPhoneNumber = val
    }

    function setCoparentDecliningCall(videoCallId?: number) {
      if (state.activeCall?.itemID == videoCallId) {
        state.videoCallDeclined = true
      } else {
        state.videoCallDeclined = false
      }
    }

    function setPhoneSessionError(error: CreateSessionError | null) {
      state.phoneSessionError = error
    }

    return {
      fetchCalls,
      fetchVoicemails,
      fetchUnseenVoicemails,
      fetchCoparentCallingSettings,
      fetchHasCompleteCallingSetup,
      completeCallingSetup,
      skipCallingSetup,
      sendCoparentReminder,
      fetchAllowsVideoCalls,
      setAllowsVideoCalls,
      buyMinutesPost,
      fetchCallAddonMinutesPlans,
      fetchCallLogs,
      fetchCallLog,
      fetchCallLogRecordingInfo,
      fetchRecordingSasUrl,
      fetchTranscriptSegments,
      fetchTranscriptJobStatus,
      firstLegPost,
      firstLegGather,
      firstLegHold,
      firstLegConnect,
      firstLegCallback,
      outOfSessionPost,
      recordingCallback,
      fetchDownloadUrl,
      fetchPreviewdUrl,
      secondLegPost,
      secondLegGather,
      secondLegCallback,
      sessionPost,
      createRoom,
      joinRoom,
      completeRoom,
      updateCall,
      setStartVideoCall,
      isBrowserSupportedForTwilio,
      getActiveVideoCall,
      newCallIsAvailable,
      setCallingItems,
      resetCallingItems,
      setSelectedCallingItem,
      setLoadMoreCallingItems,
      setSelectedCallingFilter,
      fetchCallingItems,
      setPageNumber,
      fetchMoreCallingItems,
      refreshNewCallingItems,
      fetchCallingItemsByPage,
      groupedCallingItems,
      fetchPhoneCallDetails,
      isFirstInGroup,
      isActiveCallIncoming,
      getVideoCallRecordingURL,
      getVideoCall,
      getVideoCallTranscript,
      startCheckingForNewCall,
      getVideoVoicemail,
      getVideoVoicemailTranscript,
      fetchVideoCallDetails,
      setShowDetailsMenu,
      getActiveVideoCallByItemID,
      setShowVideoCall,
      setShowVideoCallConfirmation,
      logAction,
      updateVideoCallStatus,
      updateVoicemailStatus,
      setActiveCall,
      setHasCompletedCallingSetup,
      setShowCallingSetup,
      setSentReminderWhen,
      bothParentsPassPrecheck,
      setShowAudioCall,
      singleCallReportPost,
      singleVideoCallReportPost,
      singleVoicemailReportPost,
      startDownload,
      handleDownloadedFile,
      stopDownload,
      removeDownloadFromList,
      removeAllDownloads,
      cancelAllDownloads,
      getInProgressDownloads,
      reDownload,
      getVideoCallRecording,
      getVideoVoicemailRecording,
      initialLoad,
      setMediatId,
      setVideoCallThreadId,
      setCallThreadId,
      setProxyPhoneNumber,
      fetchUnseenVoicemailCount,
      updateVoicemailIsUnseenListItem,
      setCallingSearchTerm,
      reset,
      ...toRefs(state),
      ignoreFilterAndSearchUpdates,
      resetMainCallingFeature,
      setCoparentDecliningCall,
      getTestAccessToken,
      setIntroVideoDiagnostics,
      setShowVideoCallError,
      tryShowVideoCall,
      showMobileHeaderCog,
      setCanCallStatus,
      resetCanCallStatus,
      setPhoneSessionError
    }
  },
  { persist: { pick: paths } }
)
